import { AppDispatch, RootState } from "@/services/state/store";
import { fetchWebApiByUuid } from "../../services/state/web-api/web-api-async-calls";
import { useDisclosure, useToast } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  WebApi,
  WebApiHeader,
  WebApiQueryParam,
} from "@/services/state/web-api/web-api-interface";
import { setSelectedWebApi } from "../../services/state/web-api/web-api";
import { getAxiosClient } from "../../services/HttpsService";
import { useMonaco } from "@monaco-editor/react";
import { fetchRules } from "../../services/state/rules/rules-async-calls";
import { fetchProcesses } from "../../services/state/processes/processes-async-calls";

export const useWebApi = () => {
  const { webApiId } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const monaco = useMonaco();
  const toast = useToast();

  const axiosClient = getAxiosClient();

  const [selectedMethod, setSelectedMethod] = useState<string>("GET");
  const [selectedContentType, setSelectedContentType] =
    useState<string>("application/json");
  const [body, setBody] = useState<string>("");
  const [requestBody, setRequestBody] = useState<string>("{}");
  const [requestBodyMarker, setRequestBodyMarker] = useState<Object[]>([]);
  const [headers, setHeaders] = useState<WebApiHeader[]>([]);
  const [queryParams, setQueryParams] = useState<WebApiQueryParam[]>([]);
  const [response, setResponse] = useState<string | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const selectedWebApi = useSelector(
    (state: RootState) => state.webApi.selectedWebApi
  );
  const selectedWebApiBody = useSelector(
    (state: RootState) => state.webApi.selectedWebApiBody?.apiContent
  );
  const selectedWebApiHeaders = useSelector(
    (state: RootState) => state.webApi.selectedWebApiHeaders
  );
  const selectedWebApiQueryParams = useSelector(
    (state: RootState) => state.webApi.selectedWebApiQueryParams
  );
  const processes = useSelector(
    (state: RootState) => state.processes.processes
  );
  const processVariables = useSelector(
    (state: RootState) => state.processModeler.processVariables
  );
  const rules = useSelector((state: RootState) => state.rules.rules);

  const { isOpen: isEditing, onToggle: onEditToggle } = useDisclosure();

  const processMatchers = processes.map((process) => ({
    name: `pm!${process.processNm.replace(/ /g, "_")}`,
    value: `${process.uuid}`,
  }));

  const ruleMatchers = rules.map((rule) => ({
    name: `rule!${rule.ruleNm.replace(/ /g, "_")}`,
    value: `rule!${rule.uuid}`,
  }));

  const pvMatchers = processVariables.map((pv) => ({
    name: `pv!${pv.varNm.replace(/ /g, "_")}`,
    value: `pv!${pv.uuid}`,
  }));

  const methods = ["GET", "POST", "PUT", "PATCH", "DELETE"];

  const contentTypes = [
    "application/json",
    "application/x-www-form-urlencoded",
    "multipart/form-data",
  ];

  useEffect(() => {
    if (webApiId) {
      dispatch(fetchWebApiByUuid(webApiId));
    }
  }, [webApiId]);

  useEffect(() => {
    if (selectedWebApi) {
      setSelectedMethod(selectedWebApi.apiMethod);
      dispatch(fetchProcesses(selectedWebApi.app.uuid));
      dispatch(fetchRules(selectedWebApi.app.uuid));
      setHeaders(
        selectedWebApi.headers.map((header) => ({
          ...header,
          id: header.id || Math.random(),
        }))
      );
      setQueryParams(
        selectedWebApi.queryParams.map((query) => ({
          ...query,
          id: query.id || Math.random(),
        }))
      );
    }
  }, [selectedWebApi]);

  useEffect(() => {
    if (selectedWebApi && selectedWebApi.body) {
      console.log("triggered", [
        selectedWebApi,
        pvMatchers,
        ruleMatchers,
        processMatchers,
      ]);
      const expression = expressionHandlerReverse(
        selectedWebApi.body.apiContent
      );
      setBody(expression || "");
    }
  }, [
    selectedWebApi,
    pvMatchers.length,
    ruleMatchers.length,
    processMatchers.length,
  ]);

  const editorOptions = {
    selectOnLineNumbers: true,
    minimap: { enabled: false },
    readOnly: !isEditing,
  };

  const onValueChange = (
    feild: "apiNm" | "apiDesc" | "endPoint" | "relativePath",
    value: string
  ) => {
    if (selectedWebApi) {
      const selectedWebApiCopy = { ...selectedWebApi };
      selectedWebApiCopy[feild] = value;
      dispatch(setSelectedWebApi(selectedWebApiCopy));
    }
  };

  const handleAddHeader = () => {
    setHeaders((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        apiUuid: selectedWebApi?.uuid || "",
        headNm: "",
        headVal: "",
        isActive: "Y",
        createdBy: "",
        createdOn: "",
        updatedBy: "",
        updatedOn: "",
      },
    ]);
  };

  const handleRemoveHeader = (id: number) => {
    setHeaders((prev) =>
      prev.map((header) =>
        header.id === id ? { ...header, isActive: "N" } : header
      )
    );
  };

  const handleHeaderChange = (
    id: number,
    field: "headNm" | "headVal",
    value: string
  ) => {
    setHeaders((prev) =>
      prev.map((header) =>
        header.id === id ? { ...header, [field]: value } : header
      )
    );
  };

  const moveHeader = (index: number, direction: "up" | "down") => {
    const newHeaders = [...headers];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= headers.length) return;
    [newHeaders[index], newHeaders[targetIndex]] = [
      newHeaders[targetIndex],
      newHeaders[index],
    ];
    setHeaders(newHeaders);
  };

  const handleAddQueryParam = () => {
    setQueryParams([
      ...queryParams,
      {
        id: queryParams.length + 1,
        apiUuid: selectedWebApi?.uuid || "",
        paramNm: "",
        paramVal: "",
        paramSeq: queryParams.length,
        isActive: "Y",
        createdBy: "",
        createdOn: "",
        updatedBy: "",
        updatedOn: "",
      },
    ]);
  };

  const handleRemoveQueryParam = (id: number) => {
    setQueryParams((prev) =>
      prev.map((query) =>
        query.id === id ? { ...query, isActive: "N" } : query
      )
    );
  };

  const handleQueryParamChange = (
    id: number,
    field: "paramNm" | "paramVal",
    value: string
  ) => {
    setQueryParams(
      queryParams.map((query) =>
        query.id === id ? { ...query, [field]: value } : query
      )
    );
  };

  const moveQueryParam = (index: number, direction: "up" | "down") => {
    const newQueryParams = [...queryParams];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= queryParams.length) return;
    [newQueryParams[index], newQueryParams[targetIndex]] = [
      newQueryParams[targetIndex],
      newQueryParams[index],
    ];
    setQueryParams(newQueryParams);
  };

  const isEdited = true;

  const onTest = async () => {
    setIsTesting(true);
    setResponse(null);
    const data = JSON.parse(requestBody);
    const params: { [key: string]: string } = {};
    queryParams.forEach((param) => {
      params[param.paramNm] = param.paramVal;
    });
    const headers_: { [key: string]: string } = {};
    headers.forEach((header) => {
      headers_[header.headNm] = header.headVal;
    });

    try {
      const response = await axiosClient({
        method: selectedMethod,
        url: "main/webapi/" + selectedWebApi?.relativePath,
        data: data,
        headers: {
          "Content-Type": selectedContentType,
          ...headers_,
        },
        params: params,
      });
      setResponse(response.data);
      setIsTesting(false);
    } catch (error) {
      console.error("Error during API call:", error);
      setIsTesting(false);
      setResponse("Error: " + error);
    }
  };

  const onSave = async () => {
    if (!selectedWebApi) return;
    if (selectedWebApi.relativePath === "") {
      toast({
        title: "Error",
        description: "Please enter a valid relative path.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    if (selectedWebApi.apiNm === "") {
      toast({
        title: "Error",
        description: "Please enter a valid API name.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    try {
      setIsSaving(true);
      const data = {
        apiUuid: selectedWebApi.uuid,
        apiNm: selectedWebApi.apiNm,
        apiTyp: selectedWebApi.apiTyp,
        apiDesc: selectedWebApi.apiDesc || "",
        apiMethod: selectedMethod,
        endPoint: "",
        relativePath: selectedWebApi.relativePath,
        body: expressionHandler(body),
        headers: headers.map((header) => ({
          apiUuid: selectedWebApi.uuid,
          headNm: header.headNm,
          headVal: header.headVal,
        })),
        queryParams: queryParams.map((query) => ({
          apiUuid: selectedWebApi.uuid,
          paramNm: query.paramNm,
          paramVal: query.paramVal,
        })),
        isActive: "Y",
      };
      const response = await axiosClient.put("api/webapi", data);
      if (response.status === 200) {
        toast({
          title: "Success",
          description: "Web API updated successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to update Web API.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
      dispatch(fetchWebApiByUuid(selectedWebApi.uuid));
    } catch (error) {
      console.error("Error during API call:", error);
      toast({
        title: "Error",
        description: "Failed to update Web API.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    }
    setIsSaving(false);
  };

  const defineGroovyLanguage = (monacoInstance: any) => {
    if (!monacoInstance) return;

    // Register Groovy language
    monacoInstance.languages.register({ id: "groovy" });

    // Define the syntax highlighting rules using Monarch tokens
    monacoInstance.languages.setMonarchTokensProvider("groovy", {
      tokenizer: {
        root: [
          [
            /\b(def|class|if|else|while|for|return|import|package|public|private|protected|static|void|true|false|null)\b/,
            "keyword",
          ],
          [/pv![a-zA-Z_$][\w$]*(?:\s*\([^\)]*\))?/, "constant"],
          [/pm![a-zA-Z_$][\w$]*(?:\s*\([^\)]*\))?/, "constant"],
          [/(rule![a-zA-Z_][a-zA-Z0-9_]*|exp!)(?=\s*\([^)]*\))/, "comment"],
          [/[{}()\[\]]/, "@brackets"],
          [/"/, "string", "@string"],
          [/\b\d+(\.\d+)?\b/, "number"],
          [/\/\*[\s\S]*?\*\//, "comment"],
          [/\/\/.*$/, "comment"],
          [
            /\b(dbService|process|start|writeRecord|queryRecords|updateRecord|deleteRecord)\b/,
            "string",
          ],
        ],
        string: [
          [/[^\\"]+/, "string"],
          [/\\./, "string.escape"],
          [/"/, "string", "@pop"],
        ],
      },
    });

    // Define custom theme for Groovy syntax highlighting
    monacoInstance.editor.defineTheme("custom-theme", {
      base: "vs",
      inherit: true,
      rules: [
        { token: "keyword", foreground: "0000FF" },
        { token: "constant", foreground: "008000", fontStyle: "italic" },
        { token: "comment", foreground: "FFA500", fontStyle: "italic" },
        { token: "string", foreground: "008080" },
        { token: "number", foreground: "008000" },
      ],
      colors: {
        "editor.foreground": "#000000",
        "editor.background": "#FFFFFF",
      },
    });

    // Register completion provider for custom rule suggestion
    const provider = monacoInstance.languages.registerCompletionItemProvider(
      "groovy",
      {
        triggerCharacters: ["!", "(", " ", "."],
        provideCompletionItems: (model: any, position: any) => {
          const textBeforeCursor = model.getValueInRange({
            startLineNumber: position.lineNumber,
            startColumn: Math.max(1, position.column - 20),
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          });

          // Suggest pm! unique matches
          if (textBeforeCursor.endsWith("pm!")) {
            return {
              suggestions: processMatchers.map((suggestion) => ({
                label: suggestion.name,
                kind: monacoInstance.languages.CompletionItemKind.Text,
                insertText: suggestion.name,
                range: {
                  startLineNumber: position.lineNumber,
                  startColumn: position.column - 3,
                  endLineNumber: position.lineNumber,
                  endColumn: position.column,
                },
              })),
            };
          }

          // Suggest rule! unique matches
          if (textBeforeCursor.endsWith("rule!")) {
            return {
              suggestions: ruleMatchers.map((suggestion) => ({
                label: suggestion.name,
                kind: monacoInstance.languages.CompletionItemKind.Text,
                insertText: suggestion.name,
                range: {
                  startLineNumber: position.lineNumber,
                  startColumn: position.column - 5,
                  endLineNumber: position.lineNumber,
                  endColumn: position.column,
                },
              })),
            };
          }

          // Suggest pv! unique matches
          if (textBeforeCursor.endsWith("pv!")) {
            return {
              suggestions: pvMatchers.map((suggestion) => ({
                label: suggestion.name,
                kind: monacoInstance.languages.CompletionItemKind.Text,
                insertText: suggestion.name,
                range: {
                  startLineNumber: position.lineNumber,
                  startColumn: position.column - 3,
                  endLineNumber: position.lineNumber,
                  endColumn: position.column,
                },
              })),
            };
          }

          if (textBeforeCursor.endsWith("dbService.")) {
            const dbServiceMethods = [
              {
                label: "writeRecord",
                documentation: "Writes a new record into the database.",
                detail:
                  "writeRecord(String recordUuid, Map<String, Object> record)",
              },
              {
                label: "queryRecords",
                documentation:
                  "Queries records from the database based on a condition.",
                detail: "queryRecords(String recordUuid, String condition)",
              },
              {
                label: "updateRecord",
                documentation: "Updates records in the database.",
                detail:
                  "updateRecord(String recordUuid, Map<String, Object> updates, String condition)",
              },
              {
                label: "deleteRecord",
                documentation: "Deletes records from the database.",
                detail: "deleteRecord(String recordUuid, String condition)",
              },
            ];

            return {
              suggestions: dbServiceMethods.map((method) => ({
                label: method.label,
                kind: monacoInstance.languages.CompletionItemKind.Method,
                insertText: `${method.label}($0)`,
                insertTextRules:
                  monacoInstance.languages.CompletionItemInsertTextRule
                    .InsertAsSnippet,
                range: {
                  startLineNumber: position.lineNumber,
                  startColumn: position.column,
                  endLineNumber: position.lineNumber,
                  endColumn: position.column,
                },
                documentation: method.documentation,
                detail: method.detail,
              })),
            };
          }

          if (textBeforeCursor.endsWith("process.")) {
            const processMethods = [
              {
                label: "start",
                documentation: "Method to start a process.",
                detail:
                  "start(String processModel, HashMap<String, Object> processParameters, ResponseEntity<Object> onSuccess, ResponseEntity<Object> onError)",
              },
            ];

            return {
              suggestions: processMethods.map((method) => ({
                label: method.label,
                kind: monacoInstance.languages.CompletionItemKind.Method,
                insertText: `${method.label}($0)`,
                insertTextRules:
                  monacoInstance.languages.CompletionItemInsertTextRule
                    .InsertAsSnippet,
                range: {
                  startLineNumber: position.lineNumber,
                  startColumn: position.column,
                  endLineNumber: position.lineNumber,
                  endColumn: position.column,
                },
                documentation: method.documentation,
                detail: method.detail,
              })),
            };
          }

          return { suggestions: [] };
        },
      }
    );

    return () => {
      provider.dispose();
    };
  };

  useEffect(() => {
    if (monaco) {
      const dispose = defineGroovyLanguage(monaco);
      return dispose;
    }
  }, [monaco, ruleMatchers, pvMatchers]);

  const expressionHandler = (expression: string | undefined) => {
    const matchers = [...processMatchers, ...ruleMatchers, ...pvMatchers];

    matchers.sort((a, b) => b.name.length - a.name.length);

    matchers.forEach((match) => {
      const regex = new RegExp(match.name, "g");
      expression = expression?.replace(regex, `"${match.value}"`);
    });

    return expression;
  };

  function expressionHandlerReverse(expression: string | undefined) {
    const uuidRegex =
      /"\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}\b"/;

    const matches = expression?.match(uuidRegex);
    const matchers = [...processMatchers, ...ruleMatchers, ...pvMatchers];

    matches?.forEach((match) => {
      const it = matchers.find((it) => {
        return `"${it.value}"` === match;
      });

      if (it) {
        expression = expression?.replace(match, it.name);
      }
    });

    return expression;
  }

  return {
    selectedWebApi,
    isEditing,
    onEditToggle,
    isEdited,
    selectedWebApiBody,
    editorOptions,
    selectedMethod,
    setSelectedMethod,
    methods,
    selectedContentType,
    setSelectedContentType,
    contentTypes,
    body,
    setBody,
    requestBody,
    setRequestBody,
    requestBodyMarker,
    setRequestBodyMarker,
    headers,
    setHeaders,
    selectedWebApiHeaders,
    queryParams,
    setQueryParams,
    onValueChange,
    handleAddHeader,
    handleRemoveHeader,
    handleHeaderChange,
    moveHeader,
    handleAddQueryParam,
    handleRemoveQueryParam,
    handleQueryParamChange,
    moveQueryParam,
    onTest,
    onSave,
    isTesting,
    isSaving,
    response,
  };
};
